import { createContext, useContext, useEffect, useRef, useState } from "react"
import { useLocation, useNavigationType } from "react-router"

const AppScrollStrategyContext = createContext<ScrollStrategy | null>(null)

export interface ScrollStrategy {
  scrollToTop(): void
  scrollTo(position: number): void
  scrollTarget: {
    ref: React.MutableRefObject<HTMLElement | Window | null>
    disabled: boolean
  }
}

const AppScrollStrategyProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const navigationType = useNavigationType()
  const location = useLocation()

  const scrollRef = useRef<HTMLElement | Window | null>(window)

  const scrollTarget = {
    ref: scrollRef,
    disabled: false,
  }

  function scrollTo(position: number) {
    scrollTarget.ref.current?.scrollTo({ top: position, behavior: "smooth" })
  }

  function scrollToTop() {
    scrollTarget.ref.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  useEffect(() => {
    if (!scrollTarget.disabled) {
      scrollTarget.ref.current = window
    }
    // no dependency array to cause it to run on every render
  })

  useEffect(() => {
    if (
      !!scrollTarget.ref.current &&
      !scrollTarget.disabled &&
      ["PUSH", "REPLACE"].includes(navigationType)
    ) {
      scrollToTop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationType, location.pathname, location.search, scrollTarget.ref])

  const contextValue = { scrollToTop, scrollTo, scrollTarget }

  return (
    <AppScrollStrategyContext.Provider value={contextValue}>
      {children}
    </AppScrollStrategyContext.Provider>
  )
}

export const useAppScrollStrategy = (): ScrollStrategy => {
  const context = useContext(AppScrollStrategyContext)
  if (!context) {
    throw new Error(
      "useAppScrollStrategy must be used within an AppScrollStrategyProvider",
    )
  }
  return context
}

/**
 *
 * @param {object} options - Configuration options for the scroll strategy.
 * @param {boolean} [options.scrollOnMount] - If true, scrolls to the top when the component mounts.
 *
 * This hook disables the automatic scroll-to-top behavior on route changes when mounted.
 * It also provides an option to scroll to the top immediately upon mounting if `scrollOnMount` is set to true.
 * The scroll behavior is managed through the `AppScrollStrategyContext`,
 * allowing for flexible control over scrolling in different components.
 *
 * Once the component using this hook is unmounted, the scroll strategy is re-enabled,
 * allowing other components to scroll to the top on route changes as usual.
 */
export const useDisableScrollStrategy = (options: {
  scrollOnMount?: boolean
}) => {
  const { scrollTarget } = useAppScrollStrategy()
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    const currentRef = scrollTarget.ref.current
    if (currentRef) {
      scrollTarget.disabled = true
    }
    return () => {
      if (scrollTarget.disabled) {
        scrollTarget.disabled = false
      }
    }
  }, [scrollTarget, scrollTarget.ref])

  useEffect(() => {
    if (
      !!options.scrollOnMount &&
      scrollTarget.ref.current &&
      scrollTarget.disabled &&
      !hasMounted
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" })
      setHasMounted(true)
    }
  }, [
    hasMounted,
    options.scrollOnMount,
    scrollTarget.disabled,
    scrollTarget.ref,
  ])
}

export default AppScrollStrategyProvider
